import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NoticeCard from "../NoticeCard";
import PageSection from "../PageSection";
import { Subcircle } from "../../hooks/useSubcircles";
import { lightTheme } from "../../styles/theme";
import { resolveNoticeAudienceSize } from "../../utils/viewCount";
import { isStaffOnlyNotice } from "../../utils/noticeScheduling";
import type { NoticeWithType } from ".";

interface PublishedNoticesListProps {
  notices: NoticeWithType[];
  onNoticeClick: (notice: NoticeWithType) => void;
  onDuplicateClick: (notice: NoticeWithType, e: React.MouseEvent) => void;
  onSendReminderClick: (
    notice: NoticeWithType,
    e: React.MouseEvent
  ) => void;
  sendingReminderNoticeId?: string | null;
  getReadableSenderName: (sender: string | undefined) => string;
  schoolName: string;
  subcircles: Subcircle[];
  /** Org member count — denominator for org-wide notices (subcircle notices use circle memberCount). */
  orgMemberCount?: number;
}

const actionTileSx = (filled: boolean) => ({
  width: 76,
  height: 76,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  p: 0.75,
  border: "1px solid",
  borderColor: "primary.main",
  borderRadius: 2,
  bgcolor: filled ? "primary.main" : "background.paper",
  color: filled ? "common.white" : "primary.main",
  cursor: "pointer",
  transition:
    "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
  "&:hover:not(:disabled)": filled
    ? {
        bgcolor: "primary.dark",
        borderColor: "primary.dark",
        color: "common.white",
      }
    : {
        bgcolor: "primary.main",
        borderColor: "primary.main",
        color: "common.white",
      },
  "&:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
  },
});

const PublishedNoticesList: React.FC<PublishedNoticesListProps> = ({
  notices,
  onNoticeClick,
  onDuplicateClick,
  onSendReminderClick,
  sendingReminderNoticeId = null,
  getReadableSenderName,
  schoolName,
  subcircles,
  orgMemberCount = 0,
}) => {
  const theme = useTheme();

  if (notices.length === 0) {
    return (
      <PageSection>
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography color="text.secondary" variant="body1">
            No notices match your search.
          </Typography>
        </Box>
      </PageSection>
    );
  }

  return (
    <PageSection>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {notices.map((notice) => (
          <Paper
            key={notice.noticeId}
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.25 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: "none",
              cursor: "pointer",
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              display: "flex",
              alignItems: "stretch",
              gap: 0.75,
              overflow: "hidden",
              bgcolor: "background.paper",
              "&:hover": {
                borderColor: "primary.main",
                boxShadow: `0 2px 10px ${theme.palette.shadows.dark}`,
              },
            }}
            onClick={() => onNoticeClick(notice)}
          >
            <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
              <NoticeCard
                variant="list"
                title={notice.title}
                body={notice.body}
                createdAt={notice.createdAt}
                sender={getReadableSenderName(notice.sender)}
                type={notice.type}
                isUrgent={notice.isUrgent}
                isDraft={notice.isDraft}
                publishStatus={notice.publishStatus}
                publishAt={notice.publishAt}
                staffOnly={notice.staffOnly}
                targetSubcircle={notice.targetSubcircle}
                attachments={notice.attachments}
                schoolName={schoolName}
                subcircles={subcircles}
                viewCount={{
                  seen: notice.readCount ?? 0,
                  total:
                    (notice as NoticeWithType & { audienceTotal?: number })
                      .audienceTotal ??
                    resolveNoticeAudienceSize(
                      notice.targetSubcircle,
                      subcircles,
                      orgMemberCount,
                      notice.targetUserId
                    ),
                }}
              />
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "center",
                gap: 1,
                pl: { xs: 0, md: 1 },
                pt: { xs: 1.5, md: 0 },
                borderTop: {
                  xs: `1px solid ${lightTheme.colors.borderSlateLight}`,
                  md: "none",
                },
                borderLeft: {
                  md: `1px solid ${lightTheme.colors.borderSlateLight}`,
                },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  justifyContent: { xs: "flex-start", md: "center" },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label="Duplicate notice"
                  onClick={(e) => onDuplicateClick(notice, e)}
                  sx={actionTileSx(false)}
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: 24 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      lineHeight: 1.15,
                      textAlign: "center",
                    }}
                  >
                    Duplicate
                  </Typography>
                </Box>
                {!isStaffOnlyNotice(notice) && (
                  <Box
                    component="button"
                    type="button"
                    aria-label="Send reminder"
                    onClick={(e) => onSendReminderClick(notice, e)}
                    disabled={
                      Boolean(sendingReminderNoticeId) &&
                      sendingReminderNoticeId !== notice.noticeId
                    }
                    sx={actionTileSx(false)}
                  >
                    {sendingReminderNoticeId === notice.noticeId ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      <NotificationsActiveOutlinedIcon sx={{ fontSize: 24 }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.625rem",
                        fontWeight: 600,
                        lineHeight: 1.15,
                        textAlign: "center",
                      }}
                    >
                      Send reminder
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </PageSection>
  );
};

export default PublishedNoticesList;
